import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Alert } from "@mui/material";
import {
  AlternateEmail,
  ArrowBackRounded,
  PermIdentity,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import AuthLayout, { authStyles as styles } from "../../components/customComponents/authLayout/AuthLayout";
import OtpBoxInput from "../../components/customComponents/otpInput/OtpBoxInput";
import { authApi } from "../../auth/authApi";
import { SELF_SIGNUP_ENABLED } from "../../auth/config";
import { loginConfig } from "../../auth/config";
import { setAuth } from "../../store/actions";
import {
  authErrorMessage,
  authStateFromTokenResponse,
  isValidEmail,
  maskEmail,
  validatePassword,
} from "../../utils/authSession";
import { emailDeliveryFailure } from "../../utils/emailDeliveryResponse";

const OTP_LENGTH = 6;
const FALLBACK_RESEND_SECONDS = 60;

/**
 * Self-signup. Reachable only when `loginConfig.selfSignup` is true (auth/config.js) - the
 * same mechanism that restricts login methods for a white-label build also decides whether this
 * flow, and its landing-page card, exist at all. SignupRoute (auth/ConfiguredLogin.jsx) enforces
 * this at the router level too, so this component's own guard below is defense in depth, not the
 * only gate.
 *
 * The frontend flag only hides the UI; it is not a security control - the backend's own
 * self-signup flag is expected to independently refuse the request server-side.
 */
export default function Signup() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const accessToken = useSelector((state) => state.auth?.accessToken);

  const [step, setStep] = useState("details"); // "details" | "verify"
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  if (!SELF_SIGNUP_ENABLED || !loginConfig.selfSignup) {
    return <Navigate to="/" replace />;
  }

  if (accessToken) {
    return <Navigate to="/splash" replace />;
  }

  const handleRequestOtp = async (event) => {
    if (event) event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!isValidEmail(normalizedEmail)) {
      setError("Enter a valid email address.");
      return;
    }
    if (!firstName.trim()) {
      setError("Enter your first name.");
      return;
    }

    setError("");
    setNotice("");
    setSubmitting(true);
    try {
      const res = await authApi.signupRequest({
        email: normalizedEmail,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      const data = res?.data || {};
      const deliveryError = emailDeliveryFailure(data);
      if (deliveryError) { setError(deliveryError); return; }
      setMaskedEmail(data.maskedEmail || maskEmail(normalizedEmail));
      setCooldown(Number(data.resendAfterSeconds) || FALLBACK_RESEND_SECONDS);
      setNotice(data.message || "We've emailed you a 6-digit code.");
      setOtp("");
      setStep("verify");
    } catch (err) {
      console.error("Signup request failed:", err);
      setError(authErrorMessage(err, "We couldn't start your sign-up."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    if (otp.length !== OTP_LENGTH) return;
    const problem = validatePassword(password, confirmPassword);
    if (problem) {
      setError(problem);
      return;
    }

    setError("");
    setSubmitting(true);
    try {
      const res = await authApi.signupVerify({
        email: email.trim().toLowerCase(),
        otp,
        password,
        confirmPassword,
      });
      dispatch(setAuth(authStateFromTokenResponse(res?.data)));
      navigate("/splash", { replace: true }); // splash routes to the role's dashboard
    } catch (err) {
      console.error("Signup verification failed:", err);
      setError(authErrorMessage(err, "We couldn't create your account."));
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      heading={step === "details" ? "Create your account" : "Confirm your email"}
      subheading={
        step === "details"
          ? "We'll email you a code to confirm the address, then you choose a password."
          : undefined
      }
      footer={
        <div className={styles.footerRow}>
          <button type="button" className={styles.linkButton} onClick={() => navigate("/")}>
            <ArrowBackRounded fontSize="inherit" /> Back to sign in
          </button>
        </div>
      }
    >
      {step === "details" ? (
        <form className={styles.form} onSubmit={handleRequestOtp}>
          <div className={styles.fieldGroup}>
            <label className={styles.visuallyHidden} htmlFor="signup-first-name">
              First name
            </label>
            <input
              id="signup-first-name"
              type="text"
              placeholder="First name"
              className={styles.inputField}
              value={firstName}
              onChange={(e) => {
                setError("");
                setFirstName(e.target.value);
              }}
              autoComplete="given-name"
              disabled={submitting}
              autoFocus
            />
            <PermIdentity className={styles.trailingIcon} />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.visuallyHidden} htmlFor="signup-last-name">
              Last name
            </label>
            <input
              id="signup-last-name"
              type="text"
              placeholder="Last name"
              className={styles.inputField}
              value={lastName}
              onChange={(e) => {
                setError("");
                setLastName(e.target.value);
              }}
              autoComplete="family-name"
              disabled={submitting}
            />
            <PermIdentity className={styles.trailingIcon} />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.visuallyHidden} htmlFor="signup-email">
              Email
            </label>
            <input
              id="signup-email"
              type="email"
              placeholder="Email address"
              className={styles.inputField}
              value={email}
              onChange={(e) => {
                setError("");
                setEmail(e.target.value.trim());
              }}
              autoComplete="email"
              disabled={submitting}
            />
            <AlternateEmail className={styles.trailingIcon} />
          </div>

          {error && (
            <Alert severity="error" className={styles.alert}>
              {error}
            </Alert>
          )}

          <button
            type="submit"
            className={styles.primaryButton}
            disabled={!email || !firstName || submitting}
          >
            {submitting ? "Sending…" : "Send code"}
          </button>
        </form>
      ) : (
        <form className={styles.form} onSubmit={handleVerify}>
          <p className={styles.otpMeta}>
            Enter the {OTP_LENGTH}-digit code sent to <strong>{maskedEmail}</strong>
          </p>

          <OtpBoxInput
            value={otp}
            onChange={(next) => {
              setError("");
              setOtp(next);
            }}
            numInputs={OTP_LENGTH}
            disabled={submitting}
          />

          <div className={styles.resendRow}>
            <span>Didn't get the code?</span>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => handleRequestOtp()}
              disabled={cooldown > 0 || submitting}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
            </button>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.visuallyHidden} htmlFor="signup-password">
              Password
            </label>
            <input
              id="signup-password"
              type={showPassword ? "text" : "password"}
              placeholder="Choose a password"
              className={styles.inputField}
              value={password}
              onChange={(e) => {
                setError("");
                setPassword(e.target.value);
              }}
              autoComplete="new-password"
              disabled={submitting}
            />
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => setShowPassword((prev) => !prev)}
              onMouseDown={(e) => e.preventDefault()}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </button>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.visuallyHidden} htmlFor="signup-confirm-password">
              Confirm password
            </label>
            <input
              id="signup-confirm-password"
              type={showPassword ? "text" : "password"}
              placeholder="Confirm password"
              className={styles.inputField}
              value={confirmPassword}
              onChange={(e) => {
                setError("");
                setConfirmPassword(e.target.value);
              }}
              autoComplete="new-password"
              disabled={submitting}
            />
          </div>

          {notice && !error && (
            <Alert severity="info" className={styles.alert}>
              {notice}
            </Alert>
          )}
          {error && (
            <Alert severity="error" className={styles.alert}>
              {error}
            </Alert>
          )}

          <button
            type="submit"
            className={styles.primaryButton}
            disabled={otp.length !== OTP_LENGTH || !password || !confirmPassword || submitting}
          >
            {submitting ? "Creating…" : "Create account"}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
